import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Sparkles, ArrowLeft, Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const ContactPage = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.get('/cms/public/site-settings');
        
        // Process settings to convert relative URLs to absolute
        const processedSettings = { ...response.data };
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        
        // Helper to convert URLs
        const convertUrl = (url) => {
          if (!url) return url;
          if (url.startsWith('/api')) return `${backendUrl}${url}`;
          if (url.includes('drive.google.com/file/d/')) {
            const fileId = url.match(/\/d\/([^/]+)/)?.[1];
            if (fileId) return `https://drive.google.com/uc?export=view&id=${fileId}`;
          }
          return url;
        };
        
        processedSettings.logo_url = convertUrl(processedSettings.logo_url);
        setSettings(processedSettings);
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/cms/public/contact', formData);
      setSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const siteName = settings?.site_name || 'Ontrof';

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900" data-testid="contact-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-lg border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              {settings?.logo_url ? (
                <img src={settings.logo_url} alt={siteName} className="h-10 w-auto" />
              ) : (
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
              )}
              <span className="text-xl font-bold text-white">{siteName}</span>
            </Link>
            
            <div className="hidden md:flex items-center gap-6">
              <Link to="/features" className="text-white/80 hover:text-white text-sm font-medium transition-colors">Features</Link>
              <Link to="/pricing" className="text-white/80 hover:text-white text-sm font-medium transition-colors">Pricing</Link>
              <Link to="/contact" className="text-white text-sm font-medium transition-colors">Contact</Link>
            </div>

            <div className="flex items-center gap-3">
              <Button
                onClick={() => navigate('/register')}
                data-testid="nav-register-btn"
                className="bg-gradient-to-r from-cyan-400 to-blue-500 hover:from-cyan-500 hover:to-blue-600 text-white font-semibold px-5 py-2 rounded-lg shadow-lg shadow-cyan-500/25 transition-all duration-300 hover:shadow-cyan-500/40 hover:scale-105"
              >
                Register
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/login')}
                data-testid="nav-login-btn"
                className="border-2 border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:text-slate-900 font-semibold px-5 py-2 rounded-lg transition-all duration-300 hover:scale-105"
              >
                Login
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <Button 
            variant="ghost" 
            className="mb-6 text-slate-400 hover:text-white"
            onClick={() => navigate('/')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
          
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Get in Touch
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Have questions about {siteName}? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-2xl font-bold text-white mb-6">Contact Information</h2>
              
              <div className="space-y-6">
                {settings?.contact_email && (
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">Email</h3>
                      <a href={`mailto:${settings.contact_email}`} className="text-slate-400 hover:text-blue-400 transition-colors">
                        {settings.contact_email}
                      </a>
                    </div>
                  </div>
                )}

                {settings?.contact_phone && (
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-green-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">Phone</h3>
                      <a href={`tel:${settings.contact_phone}`} className="text-slate-400 hover:text-green-400 transition-colors">
                        {settings.contact_phone}
                      </a>
                    </div>
                  </div>
                )}

                {settings?.contact_address && (
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-amber-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">Address</h3>
                      <p className="text-slate-400">{settings.contact_address}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Quick Info */}
              <div className="mt-10 p-6 bg-white/5 rounded-2xl border border-white/10">
                <h3 className="text-white font-semibold mb-3">Quick Response Time</h3>
                <p className="text-slate-400 text-sm">
                  We typically respond to all inquiries within 24 hours during business days. 
                  For urgent matters, please call us directly.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <Card className="bg-white border-slate-200 shadow-2xl">
                <CardContent className="pt-6">
                  {submitted ? (
                    <div className="text-center py-10">
                      <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle className="w-8 h-8 text-green-500" />
                      </div>
                      <h3 className="text-xl font-semibold text-[#0a192f] mb-2">Message Sent!</h3>
                      <p className="text-[#0a192f]/70 mb-6">
                        Thank you for reaching out. We'll get back to you soon.
                      </p>
                      <Button 
                        variant="outline"
                        className="border-[#0a192f]/30 text-[#0a192f] hover:bg-[#0a192f]/5"
                        onClick={() => {
                          setSubmitted(false);
                          setFormData({ name: '', email: '', phone: '', company: '', message: '' });
                        }}
                      >
                        Send Another Message
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-[#0a192f]">Name *</Label>
                          <Input
                            name="name"
                            placeholder="Your name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="bg-white border-[#0a192f]/30 text-[#0a192f] placeholder:text-[#0a192f]/40 focus:border-[#0a192f] focus:ring-[#0a192f]/20"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[#0a192f]">Email *</Label>
                          <Input
                            name="email"
                            type="email"
                            placeholder="you@company.com"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="bg-white border-[#0a192f]/30 text-[#0a192f] placeholder:text-[#0a192f]/40 focus:border-[#0a192f] focus:ring-[#0a192f]/20"
                          />
                        </div>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-[#0a192f]">Phone</Label>
                          <Input
                            name="phone"
                            placeholder="+91 98765 43210"
                            value={formData.phone}
                            onChange={handleChange}
                            className="bg-white border-[#0a192f]/30 text-[#0a192f] placeholder:text-[#0a192f]/40 focus:border-[#0a192f] focus:ring-[#0a192f]/20"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[#0a192f]">Company</Label>
                          <Input
                            name="company"
                            placeholder="Your agency name"
                            value={formData.company}
                            onChange={handleChange}
                            className="bg-white border-[#0a192f]/30 text-[#0a192f] placeholder:text-[#0a192f]/40 focus:border-[#0a192f] focus:ring-[#0a192f]/20"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-[#0a192f]">Message *</Label>
                        <textarea
                          name="message"
                          placeholder="How can we help you?"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          rows={5}
                          className="w-full px-3 py-2 bg-white border border-[#0a192f]/30 rounded-md text-[#0a192f] placeholder:text-[#0a192f]/40 focus:outline-none focus:ring-2 focus:ring-[#0a192f]/20 focus:border-[#0a192f]"
                        />
                      </div>

                      <Button 
                        type="submit"
                        className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white font-semibold shadow-lg shadow-cyan-500/25"
                        disabled={submitting}
                      >
                        {submitting ? 'Sending...' : 'Send Message'}
                        <Send className="w-4 h-4 ml-2" />
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-4 sm:px-6 lg:px-8 border-t border-white/10 mt-20">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-lg flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <span className="text-white font-semibold">{siteName}</span>
            </Link>
            <div className="text-slate-400 text-sm">
              &copy; {settings?.copyright_text || '2026 Ontrof. All rights reserved.'}
            </div>
            <div className="flex items-center gap-6">
              <Link to="/features" className="text-slate-400 hover:text-white text-sm transition-colors">Features</Link>
              <Link to="/pricing" className="text-slate-400 hover:text-white text-sm transition-colors">Pricing</Link>
              <Link to="/contact" className="text-slate-400 hover:text-white text-sm transition-colors">Contact</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;
